import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import References from '../../helpers/Reference';
import { getChartType, loadMeasurers, prepareMeasurers } from '../../helpers/helpers';
import { chartBarOptions } from '../../helpers/chartoptions';
import { getChartDatasets, GetFirstValueOfBasis, setChartMax, setChartMin, setLineChartOptions } from '../ChartWindow/utils';

dayjs.extend(customParseFormat);

const COMPARISON_DATE_FORMATS = [
    'DD.MM.YYYY, HH:mm:ss',
    'D.M.YYYY, H:mm:ss',
    'DD.MM.YYYY HH:mm:ss',
    'D.M.YYYY H:mm',
    'D.M.YYYY HH:mm',
    'DD.MM.YYYY H:mm',
    'DD.MM.YYYY HH:mm',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm'
];

export const parseComparisonDate = (value) => {
    if (!value)
        return null;

    const parsedByFormat = dayjs(value, COMPARISON_DATE_FORMATS, true);
    if (parsedByFormat.isValid())
        return parsedByFormat;

    const normalized = String(value).trim().replace(/[\u00a0\u202f]/g, ' ').replace(/\s+/g, ' ').replace(',', '');
    const localeDate = normalized.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (localeDate) {
        const [, day, month, year, hour, minute, second = '0'] = localeDate;
        const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
        if (date.getFullYear() === Number(year)
            && date.getMonth() === Number(month) - 1
            && date.getDate() === Number(day)
            && date.getHours() === Number(hour)
            && date.getMinutes() === Number(minute))
            return dayjs(date);
    }

    const parsed = dayjs(normalized);
    return parsed.isValid() ? parsed : null;
};

export const formatComparisonDate = (value) => {
    if (!value)
        return null;

    const date = dayjs.isDayjs(value) ? value.toDate() : value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : dayjs(date).format(References.dateHourFormat);
};

export const displayComparisonDate = (value) => {
    const parsed = parseComparisonDate(value);
    return parsed ? parsed.format('DD.MM.YYYY HH:mm') : value;
};

const getComparisonPeriodValue = (value) => {
    const parsed = parseComparisonDate(value);
    return parsed
    ? `date:${parsed.startOf('hour').valueOf()}`
        : `raw:${String(value ?? '').trim().replace(/\s+/g, ' ')}`;
};

export const hasDifferentComparisonPeriods = (channels) => {
    if (channels.length < 2)
        return false;

    const first = channels[0];
    const firstStart = getComparisonPeriodValue(first.start);
    const firstFinish = getComparisonPeriodValue(first.finish);
    return channels.slice(1).some((channel) => (
        getComparisonPeriodValue(channel.start) !== firstStart
        || getComparisonPeriodValue(channel.finish) !== firstFinish
    ));
};

export const getSharedComparisonPeriod = (channels) => {
    if (!channels.length)
        return null;

    const first = channels[0];
    if (hasDifferentComparisonPeriods(channels))
        return null;

    const start = parseComparisonDate(first.start);
    const finish = parseComparisonDate(first.finish);
    return start && finish ? [start, finish] : null;
};

export const applyCommonComparisonPeriod = (range, setPeriodForAll) => {
    if (!range?.[0] || !range?.[1])
        return;

    const start = formatComparisonDate(range[0]);
    const finish = formatComparisonDate(range[1]);
    if (start && finish)
        setPeriodForAll(start, finish);
};

const prepareTabbedDatasets = (prepared, measurer) => {
    const definitions = References.mchs[measurer.type]?.tabs || measurer.tabs || [];
    return prepared.flatMap((tab, tabIndex) => (tab.data || []).map((values, seriesIndex) => {
        const color = References.colors[(tabIndex + seriesIndex) % References.colors.length] || '#00a';
        const title = definitions[tabIndex]?.title || measurer.typeName;
        return {
            label: `${title}${tab.data.length > 1 ? ` ${seriesIndex + 1}` : ''}`,
            fill: false,
            pointRadius: values.length === 1 ? 5 : 0,
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
            spanGaps: false,
            data: values
        };
    }));
};

const setComparisonDateAxisOptions = (options) => {
    options.scales.x.ticks = {
        ...options.scales.x.ticks,
        autoSkip: true,
        maxTicksLimit: 6,
        maxRotation: 0,
        minRotation: 0,
        callback(value) {
            const label = this.getLabelForValue(value);
            const date = parseComparisonDate(label);
            return date ? date.format('DD.MM HH:mm') : label;
        }
    };
    return options;
};

export const loadComparisonChart = async (channel, userSettings) => {
    const measurer = channel.measurer;
    const start = parseComparisonDate(channel.start);
    const finish = parseComparisonDate(channel.finish);
    if (!start || !finish)
        throw new Error('Invalid comparison period');

    const chartType = Array.isArray(measurer.chartType) && measurer.chartType.length === 0
        ? 'line'
        : getChartType(measurer.chartType) || 'line';
    const basis = GetFirstValueOfBasis(measurer.type);
    const response = await loadMeasurers(
        channel.uuid,
        'mchs',
        measurer.type,
        start.format(References.dateTimeFormat),
        finish.format(References.dateTimeFormat),
        channel.chartSettings,
        null
    );
    const prepared = prepareMeasurers(
        response,
        channel.uuid,
        'mchs',
        measurer.type,
        chartType,
        channel.chartSettings,
        measurer.settings,
        basis,
        userSettings
    );

    if (Array.isArray(prepared)) {
        const baseData = prepared[0] || { labels: [], data: [], sources: [] };
        const datasets = prepareTabbedDatasets(prepared, measurer);
        const options = setComparisonDateAxisOptions(setLineChartOptions(measurer, datasets, basis, userSettings));
        options.plugins.legend.display = datasets.length > 1;
        return {
            labels: baseData.labels || [],
            datasets,
            options,
            hasData: prepared.some((tab) => (tab.labels || []).length > 0)
        };
    }

    if (chartType === 'bar' && ['precipitation', 'precipitationduo'].includes(measurer.type)) {
        const barColors = References.mchs[measurer.type]?.bar_colors;
        const defaultColor = '#00a';
        const colors = (prepared.source || []).map((source) => (
            barColors?.colors?.[source?.[barColors.field]] || defaultColor
        ));
        const datasets = [{
            fill: false,
            pointRadius: 0,
            backgroundColor: colors,
            borderColor: '#fff',
            borderSkipped: 'right',
            borderWidth: 1,
            spanGaps: false,
            barPercentage: 1,
            categoryPercentage: 1,
            data: prepared.data || []
        }];
        const options = chartBarOptions();
        options.scales.y.min = setChartMin(prepared, measurer, basis, userSettings);
        options.scales.y.max = setChartMax(prepared, measurer, basis, userSettings);
        if (measurer.tooltips) {
            options.plugins.tooltip.callbacks = {
                footer(items) {
                    const item = items?.[0];
                    const field = barColors?.field;
                    const source = prepared.source?.[item?.dataIndex];
                    const precipitationType = source?.[field];
                    const precipitationValue = prepared.data?.[item?.dataIndex];
                    return precipitationValue > 0 ? measurer.tooltips[precipitationType] : undefined;
                }
            };
        }
        setComparisonDateAxisOptions(options);
        return {
            chartType,
            labels: prepared.labels || [],
            datasets,
            options,
            hasData: (prepared.labels || []).length > 0
        };
    }

    const datasets = getChartDatasets(prepared, measurer, basis);
    const options = setComparisonDateAxisOptions(setLineChartOptions(measurer, prepared, basis, userSettings));
    return {
        chartType,
        labels: prepared.labels || [],
        datasets,
        options,
        hasData: (prepared.labels || []).length > 0
    };
};
